import { createContext, useContext } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ImageUploadField } from "@/components/ImageUploadField";
import { Plus, Trash2 } from "lucide-react";

// Shared field components for config-driven admin editors. cfg + set come from context so
// these live at module scope (stable identity) — inputs keep focus on every keystroke.
const Ctx = createContext({ cfg: {}, set: () => {} });
export function EditorProvider({ cfg, set, children }) {
  return <Ctx.Provider value={{ cfg, set }}>{children}</Ctx.Provider>;
}
const useEd = () => useContext(Ctx);
export const getAt = (obj, path) => (Array.isArray(path) ? path : path.split(".")).reduce((o, k) => (o == null ? o : o[k]), obj);
export function setPath(obj, path, value) {
  const keys = Array.isArray(path) ? path : path.split(".");
  const clone = structuredClone(obj);
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) { if (cur[keys[i]] == null) cur[keys[i]] = {}; cur = cur[keys[i]]; }
  cur[keys[keys.length - 1]] = value;
  return clone;
}
const tid = (p) => `pg-${String(p).replaceAll(".", "-")}`;

export function F({ label, path, mono }) {
  const { cfg, set } = useEd();
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input value={getAt(cfg, path) ?? ""} onChange={(e) => set(path, e.target.value)} data-testid={tid(path)}
        className={`bg-black border-[#27272A] text-white rounded-sm ${mono ? "font-mono text-xs" : ""}`} />
    </div>
  );
}
export function A({ label, path, rows = 3 }) {
  const { cfg, set } = useEd();
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Textarea value={getAt(cfg, path) ?? ""} onChange={(e) => set(path, e.target.value)} rows={rows} data-testid={tid(path)}
        className="bg-black border-[#27272A] text-white rounded-sm text-sm" />
    </div>
  );
}
export function Img({ label, path }) {
  const { cfg, set } = useEd();
  return <ImageUploadField label={label} value={getAt(cfg, path) || ""} onChange={(url) => set(path, url)} testid={tid(path)} />;
}
export function Toggle({ path, label = "Show this section" }) {
  const { cfg, set } = useEd();
  return (
    <div className="flex items-center justify-between border border-[#27272A] bg-black px-4 py-3 rounded-sm">
      <span className="text-sm text-zinc-300">{label}</span>
      <Switch checked={getAt(cfg, path) !== false} onCheckedChange={(v) => set(path, v)} data-testid={`toggle-${tid(path)}`} />
    </div>
  );
}
export function StringList({ path, itemLabel = "Item" }) {
  const { cfg, set } = useEd();
  const arr = getAt(cfg, path) || [];
  return (
    <div className="space-y-2">
      {arr.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Textarea value={v} onChange={(e) => set(path, arr.map((x, j) => (j === i ? e.target.value : x)))} rows={2}
            className="bg-black border-[#27272A] text-white rounded-sm text-sm" />
          <Button type="button" variant="outline" onClick={() => set(path, arr.filter((_, j) => j !== i))}
            className="border-[#27272A] bg-black text-zinc-400 hover:text-red-400 rounded-sm px-2"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set(path, [...arr, ""])}
        className="border-[#27272A] bg-black text-zinc-300 hover:text-white rounded-sm text-xs"><Plus className="h-3 w-3 mr-1" /> Add {itemLabel}</Button>
    </div>
  );
}
// Generic object-array editor. fields: [{key,label,type?}]
export function ObjList({ path, fields, blank, addLabel }) {
  const { cfg, set } = useEd();
  const arr = getAt(cfg, path) || [];
  const upd = (i, key, val) => set(path, arr.map((x, j) => (j === i ? { ...x, [key]: val } : x)));
  return (
    <div className="space-y-3">
      {arr.map((item, i) => (
        <div key={i} className="border border-[#27272A] bg-black p-3 rounded-sm space-y-2 relative">
          <button type="button" onClick={() => set(path, arr.filter((_, j) => j !== i))}
            className="absolute top-2 right-2 text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          {fields.map((f) => (
            f.type === "textarea" ? (
              <div key={f.key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</Label>
                <Textarea value={item[f.key] ?? ""} onChange={(e) => upd(i, f.key, e.target.value)} rows={2} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm text-sm" />
              </div>
            ) : (
              <div key={f.key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</Label>
                <Input value={item[f.key] ?? ""} onChange={(e) => upd(i, f.key, e.target.value)} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm text-sm" />
              </div>
            )
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set(path, [...arr, { ...blank }])}
        className="border-[#27272A] bg-black text-zinc-300 hover:text-white rounded-sm text-xs"><Plus className="h-3 w-3 mr-1" /> {addLabel}</Button>
    </div>
  );
}
